import {ReportableError} from '@wix/document-manager-utils'
import type {Namespace, Pointer} from '@wix/document-services-types'
import {createImmutableProxy} from '@wix/wix-immutable-proxy'
import type {EventEmitter} from 'events'
import _ from 'lodash'
import type {CoreConfig} from '../documentManagerCore'
import type {
    BaselineBlacklistCheck,
    DocumentDataTypes,
    FilterGetter,
    IndexKey,
    PostSetOperation,
    PostTransactionOperation,
    Transaction,
    ValidationWhitelistCheck
} from '../types'
import {debug} from '../utils/debug'
import {createUniqueIdGenerator} from '../utils/uniqueIdGenerator'
import {createQueryIndex, IndexedValues, ValueToIndexIds} from './queryIndex/queryIndex'
import {createDalSchema} from './schema/dalSchema'
import {createSnapshotChain, SnapshotDal} from './snapshot/snapshotDal'
import {createList} from './snapshot/snapshotList'
import {createTagManager} from './snapshot/tagManager'
import {createStore, createStoreFromJS, DalJsStore, DalStore, DalValue, DmStore} from './store'
import {createValidator, ValidateValue} from './validation/dalValidation'
import {DAL, BaseDal, getPointersByNamespaceKeys, CreateArgs} from './baseDal'

export type SetterSet = (pointer: Pointer, value: any) => void
export type Getter = (pointer: Pointer) => DalValue
export type CustomGetter = (dal: PDAL, rootPointer: Pointer) => DalValue
export type QueryFilterGetters = Record<string, FilterGetter>
export type GetIndexed = (indexKey: IndexKey) => IndexedValues
export type DalValueChangeCallback = (pointer: Pointer, oldValue: DalValue, newValue: DalValue) => void

export type PDAL = Pick<DAL, 'queryFilterGetters' | 'query' | 'get'>

export const TransactionEvents = {
    TRANSACTION_REJECTED: 'TRANSACTION_REJECTED',
    TRANSACTION_BY_OTHER: 'TRANSACTION_BY_OTHER',
    LOCAL_TRANSACTION_APPROVED: 'LOCAL_TRANSACTION_APPROVED'
} as const

export const SNAPSHOT_REMOVED_EVENT = 'SNAPSHOT_REMOVED_DUE_TO_CONFLICT'

export type TransactionEvent = keyof typeof TransactionEvents

export interface OpenTransaction {
    id: string
    store: DmStore
    attempt: number
}

// These error constructors explicitly set the prototype since it's the recommended workaround for a TypeScript issue:
// See https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
export class CommitsDisabledError extends ReportableError {
    constructor() {
        super({
            errorType: 'CommitsDisabledError',
            message:
                'commitTransaction was called when commits were disabled. Perhaps disableCommits was called without a subsequent call to enableCommits?'
        })
        Object.setPrototypeOf(this, CommitsDisabledError.prototype)
    }
}

export class TransactionRejectionError extends Error {
    constructor(id: string, reason?: string) {
        const reasonMessage = reason ? `. Reason: ${reason}` : ''
        super(`Transaction was rejected by server, transaction id ${id}${reasonMessage}`)
        Object.setPrototypeOf(this, TransactionRejectionError.prototype)
    }
}

export class SnapshotRemovedError extends Error {
    constructor(id: string) {
        super(`Snapshot was removed by client due to conflict, snapshot correlation id ${id}`)
        Object.setPrototypeOf(this, SnapshotRemovedError.prototype)
    }
}

export const basedOnSignatureNS = 'basedOnSignature'

class Registrar {
    constructor(private dal: DocumentManagerDAL) {}

    registerFilter(name: string, filter: ValueToIndexIds): void {
        const filterFactory = this.dal._queryIndex.createFilterFactory(name, filter)
        this.dal.queryFilterGetters[filterFactory.indexName] = filterFactory.getFilter
    }
    registerValidator(name: string, validateValue: ValidateValue): void {
        this.dal.validator.registerValidator(name, validateValue)
    }
    registerRebaseValidator(name: string, validateValue: ValidateValue): void {
        this.dal.rebaseValidator.registerValidator(name, validateValue)
    }
    registerValidationWhitelistCheck(whitelistCheck: ValidationWhitelistCheck) {
        this.dal.validator.registerWhitelistCheck(whitelistCheck)
    }
    registerPostTransactionOperation(name: string, operation: PostTransactionOperation): void {
        this.dal.postTransactionOperations[name] = operation
    }
    registerBaselineBlacklistCheck(check: BaselineBlacklistCheck): void {
        this.dal.validator.registerBaselineBlacklistCheck(check)
    }
    registerForChangesCallback(callback: DalValueChangeCallback) {
        this.dal.updateCallbacks.push(callback)
    }
    unregisterForChangesCallback(callback: DalValueChangeCallback) {
        _.remove(this.dal.updateCallbacks, e => e === callback)
    }
}

class DocumentManagerDAL extends BaseDal implements DAL {
    protected debugLogger = debug('dal')
    queryFilterGetters: Record<string, FilterGetter> = {}
    tagManager = createTagManager()
    registrar = new Registrar(this)
    readonly schema
    rebaseValidator

    constructor(
        protected coreConfig: CoreConfig,
        protected postSetOperations: PostSetOperation[],
        protected dmTypes: DocumentDataTypes,
        protected customGetters: Record<string, CustomGetter>,
        protected eventEmitter: EventEmitter,
        initialStore?: DalStore
    ) {
        super(createList(), null, true)
        this.queryIndex = createQueryIndex()
        this.store = createStore()
        this.createOpenTxId = createUniqueIdGenerator(3)
        this.tentativeStore = createStore()
        this.currentOpenTransaction = this.createNewOpenTransaction()
        this.postTransactionOperations = {}
        this.commitsEnabled = true
        this.updateCallbacks = []

        this.schema = createDalSchema({
            schemaService: this.coreConfig.schemaService,
            experiments: {}
        })
        this.validator = createValidator(this.coreConfig, [basedOnSignatureNS])
        this.rebaseValidator = createValidator(this.coreConfig, [basedOnSignatureNS])

        this.createSignature = createUniqueIdGenerator(3, this.coreConfig.signatureSeed)
        if (initialStore) {
            this.mergeToApprovedStore(createStore(initialStore))
        }
    }

    protected getMergedStoreAsJson(includeCurrentTransaction: boolean): DalJsStore {
        const mergedStore = this.getMergedStore(includeCurrentTransaction) as DmStore
        return mergedStore.asJson()
    }

    /**
     * Merge all the changes since the last approved snapshot up to the new approved
     * snapshot into the main store and update the approved snapshot pointer.
     * Rebuild the tentative store with the remaining unapproved changes.
     * @param id id of the approved snapshot
     */
    approve(id: string) {
        const tx = this.snapshots.findByIdLimit(id, this.lastApproved!)
        if (!tx) {
            throw this.createCannotApproveError(id)
        }

        const diff: DmStore = createStoreFromJS(tx.mutableDiff(this.lastApproved))
        this.store.merge(diff)

        const newTentativeStore = createStoreFromJS(this.snapshots.last()?.mutableDiff(tx) as DalJsStore)
        this.tentativeStore = newTentativeStore
        this.lastApproved = tx
    }
    /**
     * Delete all the tentative snapshots from the snapshot with the given id to the last
     * approved snapshot and rebuild the tentative store with the remaining tentative snapshots
     * @param id - id of the rejected snapshot
     */
    reject(id: string) {
        const tx = this.snapshots.findByIdLimit(id, this.lastApproved!)
        if (!tx) {
            // Possibly sent and later identified as conflict, not necessarily a bug
            return
        }

        this.applyOnSnapshotsRange(this.lastApproved!, tx, (snapshot: SnapshotDal) => {
            this.snapshots.remove(snapshot)
            this.tagManager.remove(snapshot)
        })

        this.removeConflictsAndReport(TransactionEvents.TRANSACTION_REJECTED)
    }
    /**
     * Add the current open transaction to the snapshot chain and merge it to the tentative store.
     * Notify listeners of the commit
     * @param committer
     * @param skipValidations
     */
    commitTransaction(committer?: string | undefined, skipValidations?: boolean | undefined): SnapshotDal {
        if (!this.commitsEnabled) {
            this.coreConfig.logger.captureError(new CommitsDisabledError())
            return this.snapshots.last()!
        }
        const currentOpenTransactionStore = this.currentOpenTransaction.store
        if (currentOpenTransactionStore.isEmpty()) {
            return this.snapshots.last()!
        }

        const attempt = ++this.currentOpenTransaction.attempt
        if (!skipValidations) {
            this.validatePendingCommit({committer, attempt, openTxId: this.currentOpenTransaction.id})
        }

        const pointersModifiedInTheTransaction = currentOpenTransactionStore.keys()
        this.createNewOpenTransaction()

        if (this.isConflictingWithCommittedValue(currentOpenTransactionStore)) {
            this.updateIndex(pointersModifiedInTheTransaction)
            return this.snapshots.last()!
        }

        this.tentativeStore.merge(currentOpenTransactionStore)
        const snapshotId = this.snapshots.add(currentOpenTransactionStore)

        this.report(currentOpenTransactionStore, TransactionEvents.LOCAL_TRANSACTION_APPROVED)
        return snapshotId
    }
    /**
     * Insert the given snapshot into the snapshot chain at the specified position
     *
     * @param change
     * @param position
     * @param event
     * @param id
     */
    _rebase(change: DmStore, position: string, event: TransactionEvent, id: string) {
        const tx = this.snapshots.findByIdLimitInclusive(position, this.lastApproved!)
        if (!tx) {
            return this.rebaseApprovedHistory(change, position, event, id)
        }

        const newSnapshot = this.snapshots.insert(change, position, id)
        this.removeConflictsReportAndVerify(change, position, event, newSnapshot.id)
        return newSnapshot
    }
    getTentativeAndAcceptedAsTransaction(): Transaction {
        const tentativeAndAccepted = this.getMergedStore(false) as DmStore
        const transaction = {
            id: 'tentativeAndAccepted',
            items: tentativeAndAccepted.getValues()
        }
        return createImmutableProxy(transaction)
    }
    /**
     * Copies changes into the dal store. Called during DS initialization
     * @param changes - the store to copy into the dal store
     * @param label
     */
    mergeToApprovedStore(changes: DmStore, label?: string): SnapshotDal {
        const id = this.getMergeToApprovedStoreSnapshotId(changes, label)
        const clonedChanges = changes.clone()

        return this.mergeSnapshotToApprovedStore(clonedChanges, id)
    }
    enableCommits(): void {
        this.commitsEnabled = true
    }
    disableCommits(): void {
        this.commitsEnabled = false
    }
    withoutCustomGetters(func: Function) {
        return (...args: any[]) => {
            try {
                this.customGettersEnabled = false
                return func(...args)
            } finally {
                this.customGettersEnabled = true
            }
        }
    }
    hasSignature(pointer: Pointer): boolean {
        const idsWithSignature = this.dmTypes[pointer.type]?.idsWithSignature
        if (idsWithSignature) {
            return idsWithSignature.has(pointer.id)
        }
        return this.dmTypes[pointer.type]?.hasSignature ?? false
    }
    protected isRegisteredNamespace(type: string): boolean {
        return !!this.dmTypes[type]
    }
    getRegisteredTypes(): DocumentDataTypes {
        return this.dmTypes
    }

    getAllItemsInNamespace(namespace: Namespace): Pointer[] {
        const namespaceObj = this.getMergedNamespace(namespace)
        return getPointersByNamespaceKeys(Object.keys(namespaceObj), namespace)
    }

    /** Rebuild the tentative store with conflicting snapshots removed.
     *
     * Conflicting snapshots are snapshots with a broken signature chain to the committed value or snapshots that
     * lead to an invalid store when applied to the approved state.
     */
    protected removeConflicts() {
        /* To rebase we reset the dal to the approved state since it's the last "consensus" point from the server,
         * it cannot change.
         * We then re-apply the tentative transactions, which weren't approved by the server yet, one-by-one.
         * If a tentative transaction conflicts with previous values we remove it. There is no need to send it to the
         * server since we already know it's conflicting and will be rejected.
         * We refer to that situation as a "local rejection"
         */
        const openTransaction = this.currentOpenTransaction
        const droppedSnapshots: SnapshotDal[] = []
        const pendingSnapshots = createSnapshotChain(this.lastApproved, this.snapshots.last(), 'removeConflicts')
        this.resetToApprovedState()

        for (const [i, snapshot] of pendingSnapshots.entries()) {
            /* There's no need to apply the snapshot in order to check for signature conflicts.
             * But validators work on the DAL as a whole, so we have to apply the snapshot, validate and then
             * "unapply" it if a validation conflict occurred.
             * To "unapply" we rebuild the tentative store but we only use the snapshots that weren't
             * already locally rejected.
             */
            const snapshotStore = snapshot.getStore()
            if (this.isConflictingWithMergedValue(snapshotStore)) {
                droppedSnapshots.push(snapshot)
                this.removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot)
            } else {
                this.currentOpenTransaction.store.merge(snapshotStore)
                this.updateIndex(snapshotStore.keys())
                if (this.hasRebaseValidationErrors(snapshotStore)) {
                    this.debugLogger.info(
                        `dmDal rebase is removing a snapshot due to validation conflict. Snapshot id: ${snapshot.id}`
                    )
                    droppedSnapshots.push(snapshot)
                    this.removeTentativeSnapshotWithoutUpdatingTheIndex(snapshot)
                    const validPendingSnapshotsUpToThisPoint = _(pendingSnapshots)
                        .take(i)
                        .without(...droppedSnapshots)
                        .value()
                    this.rebuildOpenStore(validPendingSnapshotsUpToThisPoint)
                }
            }
        }
        this.tentativeStore.merge(this.currentOpenTransaction.store)

        this.currentOpenTransaction = openTransaction
        this.updateIndex(openTransaction.store.keys())
        if (droppedSnapshots.length) {
            this.eventEmitter.emit(SNAPSHOT_REMOVED_EVENT, droppedSnapshots)
        }
    }

    private rebaseApprovedHistory(change: DmStore, position: string, event: TransactionEvent, id?: string) {
        const tx = this.snapshots.findById(position)
        if (!tx) {
            throw this.createRebaseUnlinkedError(position, id)
        }

        if (this.isConflictingWithApprovedValue(change)) {
            throw this.createRemovedApprovedError(position, id)
        }

        this.coreConfig.logger.captureError(this.createCannotRebaseError(position, id))

        const approvedLater: DmStore = _.reduce(
            createSnapshotChain(tx, this.lastApproved, 'rebaseApprovedHistory'),
            (mergedStore: DmStore, snapshotInRange: SnapshotDal) => {
                mergedStore.merge(snapshotInRange.getStore())
                return mergedStore
            },
            createStore()
        )

        const relevantChange: DmStore = createStore()
        change.forEach((pointer, value) => {
            if (!approvedLater.has(pointer)) {
                relevantChange.set(pointer, value)
            }
        })

        this.store.merge(relevantChange)
        const newSnapshot = this.snapshots.insert(change, position, id)
        this.updateIndex(relevantChange.keys())
        this.removeConflictsReportAndVerify(relevantChange, position, event, newSnapshot.id)
        return newSnapshot
    }
    private getMergeToApprovedStoreSnapshotId(changes: DmStore, label?: string): string {
        const id = this.snapshots.generateId(label)
        if (!this.tentativeStore.isEmpty() || !this.currentOpenTransaction.store.isEmpty()) {
            if (
                !changes.intersection(this.tentativeStore).isEmpty() ||
                !changes.intersection(this.currentOpenTransaction.store).isEmpty()
            ) {
                throw this.createMergeToApprovedError(changes, id)
            }
        }

        return id
    }
    private mergeSnapshotToApprovedStore(clonedChanges: DmStore, snapshotId: string): SnapshotDal {
        const newApprovedSnapshot = this.lastApproved
            ? this.snapshots.insert(clonedChanges, this.lastApproved.id, snapshotId)
            : this.snapshots.add(clonedChanges, snapshotId)

        this.lastApproved = newApprovedSnapshot
        this.store.merge(clonedChanges)
        this.updateIndex(clonedChanges.keys())
        return newApprovedSnapshot
    }
}

export const createDal = ({
    coreConfig,
    postSetOperations,
    dmTypes,
    customGetters,
    eventEmitter,
    initialStore
}: CreateArgs): DAL => {
    return new DocumentManagerDAL(
        coreConfig,
        postSetOperations,
        dmTypes,
        customGetters,
        eventEmitter,
        initialStore as DalStore
    )
}
